import { Injectable } from '@angular/core';
import { EMPTY, NEVER, Observable, of, throwError, timer } from 'rxjs';
import { delay, expand, filter, retryWhen, scan, switchMap, take, takeUntil } from 'rxjs/operators';
import { ProcessRunStatusEnum, ProcessRunStatusResult } from '@shared/models/common';
import { AuthService } from './auth.service';

export interface PollProcessStatusOptions {
  /** When this emits, polling stops and the result completes without emitting. */
  until$?: Observable<unknown>;
  /** Skip AuthService's global error popup on failed status calls (background jobs). */
  silent?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ProcessStatusPollingService {
  static readonly POLL_INTERVAL_MS = 1000;
  static readonly MAX_ATTEMPTS = 3;

  constructor(private authService: AuthService) {}

  /**
   * Polls the process status until the run is FINISHED or FAILED.
   * Emits the FINISHED status once and completes; errors when the run FAILED
   * or when status calls failed MAX_ATTEMPTS times in total.
   */
  pollUntilDone(runNum: number, options: PollProcessStatusOptions = {}): Observable<ProcessRunStatusResult> {
    const interval = ProcessStatusPollingService.POLL_INTERVAL_MS;
    const maxAttempts = ProcessStatusPollingService.MAX_ATTEMPTS;
    const getStatus = () => this.authService.getProcessStatus(runNum, options.silent);

    return getStatus().pipe(
      expand((status: ProcessRunStatusResult) =>
        this.isTerminal(status) ? EMPTY : timer(interval).pipe(switchMap(getStatus))
      ),
      retryWhen((errors) =>
        errors.pipe(
          scan((retryCount: number, err: any) => {
            if (retryCount >= maxAttempts - 1) {
              console.error(`Error occurred ${maxAttempts} times while polling run ${runNum}:`, err);
              throw err;
            }
            console.warn(`Retrying polling of run ${runNum} (${retryCount + 1}/${maxAttempts})...`);
            return retryCount + 1;
          }, 0),
          delay(interval)
        )
      ),
      filter((status: ProcessRunStatusResult) => this.isTerminal(status)),
      take(1),
      takeUntil(options.until$ ?? NEVER),
      switchMap((status: ProcessRunStatusResult) =>
        status.status === ProcessRunStatusEnum.FAILED
          ? throwError(new Error(`Process run ${runNum} failed`))
          : of(status)
      )
    );
  }

  private isTerminal(status: ProcessRunStatusResult): boolean {
    return status.status === ProcessRunStatusEnum.FINISHED || status.status === ProcessRunStatusEnum.FAILED;
  }
}
